/**
 * Client-side analytics wrapper around Datadog RUM.
 *
 * No-ops on the server. Safe to call from any client component. Use for
 * user-action telemetry (button clicks, flow completions, etc.).
 */

type Attrs = Record<string, unknown>

type RumModule = {
  datadogRum?: {
    addAction: (name: string, attrs?: Attrs) => void
    addError: (error: unknown, attrs?: Attrs) => void
    setUser: (user: { id: string } & Attrs) => void
  }
}

let mockRum: RumModule['datadogRum'] | null = null

/** Test seam: inject a mock RUM client. */
export function _setMockRum(mock: RumModule['datadogRum'] | null): void {
  mockRum = mock
}

async function getRum(): Promise<RumModule['datadogRum'] | null> {
  if (mockRum) return mockRum
  if (typeof window === 'undefined') return null
  try {
    const mod = (await import('@datadog/browser-rum')) as RumModule
    return mod.datadogRum ?? null
  } catch {
    return null
  }
}

export const analytics = {
  /**
   * Record a user action.
   *
   * Action names should be `flow.event` style: `terminate.confirm`,
   * `add_employee.step_complete`, etc.
   *
   * Do NOT pass PII in attrs (no email, DOB, SSN, names). Pass IDs and counts
   * only.
   */
  track: async (action: string, attrs?: Attrs): Promise<void> => {
    const rum = await getRum()
    if (!rum) return
    try {
      rum.addAction(action, attrs)
    } catch {
      // never fail callers on transport errors
    }
  },

  /** Identify the current user. ID only — no email or PII. */
  identify: async (userId: string, attrs?: Attrs): Promise<void> => {
    const rum = await getRum()
    if (!rum) return
    try {
      rum.setUser({ id: userId, ...(attrs ?? {}) })
    } catch {
      // never fail callers
    }
  },
}
